import React, { FC, useEffect } from 'react';
import { ScrollView } from 'react-native';
import { ColorFunction } from '@styles/colors/types';

import { useMultiMachineTimelineViewModel } from './hooks/useMultiMachineTimelineViewModel';
import {
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
  SCREEN_PADDING_VERTICAL_FOR_ITEM,
  TEST_IDS,
} from '../../constants/base.constants';
import { scale } from '@components/frame';
import DateHeader from '@components/machines/components/timeline/components/DateHeader';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import Timeline from '@components/machines/components/timeline/components/Timeline';
import ProductivityStatus from '@components/machines/components/timeline/components/ProductivityStatus';
import Counts from '@components/machines/components/timeline/components/Counts';
import NCFileDetailsModalWrapper from '@components/wallboard/components/NCFileDetailsModalWrapper';
import LabelPopup from '@components/machines/components/timeline/components/LabelPopup';
import FeatureFlags from '@components/featureFlags';
import Jobs from '@components/machines/components/timeline/components/Jobs';
import {
  useManageTimelineActions,
  useManageTimelineEffects,
  useManageTimelinePopup,
  useTimelineRange,
  useTimelineSelectors,
  useTimelineSizes,
} from '../../../../../machines/components/timeline/hooks';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { selectSelectedGroup, setSelectedChartDate, setSelectedChartDateEnd } from '@reducers/machinesV2';
import { NCFileSearchItem } from '@interfaces/search';
import { setSelectedNCFile as setSelectedNCFileManufacture } from '@reducers/manufacture';
import ColorWrapper from '@styles/colors';
import { useSelector } from 'react-redux';
import { selectMachines } from '@reducers/machines';
import { Machine, MachineGroup } from '@interfaces/machines';
import CloseButton from '@components/closeButton';
import { NCFileBatchEventTypeMenu } from '@v2/pages/machines/common/components/Header/components';
import NCFileBatchEvents from '@v2/pages/common/components/NCFileBatchEvents';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { getUTCFormatOfLocalTime, withERP } from '@helpers';
import { MULTI_TIMELINE_CONFIG } from './constants';
import { OMITTED_EVENT_MENU_ITEMS, TIMELINE_EVENTS_ORDER_BY } from '@components/machines/components/timeline/constants';

interface MultiMachineTimelineProps {
  C: ColorFunction;
  withScroll?: boolean;
  fullScreen?: boolean;
}

const MultiMachineTimeline: FC<MultiMachineTimelineProps> = ({ C, withScroll = true, fullScreen = true }) => {
  const dispatch = useAppDispatch();

  const { startTime, endTime, yearIsEnabled } = useTimelineRange();
  const { safeIsDetailsScreen } = useNavigationCheck();

  const allMachines: Machine[] = useSelector(selectMachines);
  const selectedMachineGroup: MachineGroup = useSelector(selectSelectedGroup);

  const machines =
    !safeIsDetailsScreen && selectedMachineGroup && selectedMachineGroup.machines && Array.isArray(selectedMachineGroup.machines)
      ? selectedMachineGroup.machines
      : allMachines;

  const {
    detailsWidth,
    blockWidth,
    popupWidthProductivityStatus,
    popupWidthProductivityStatusLabel,
    popupWidthProductivityStatusLabelSymbolSize,
    popupHeightProductivityStatusLabel,
    popupWidthAlarm,
    popupWidthAlarmSymbolSize,
    popupWidthNcFileName,
    popupWidthNcFileNameSymbolSize,
  } = useTimelineSizes({ fullScreen });

  const { fetchData, abortControllerEvents, abortControllerProductionBatches, machineTimelineData } = useMultiMachineTimelineViewModel({
    startTime,
    endTime,
    yearIsEnabled,
    machines,
  });

  const { clearPopupTimeout, popupConfigProductivityStatus, popupConfigProductivityStatusLabel, triggerHidePopup, triggerShowPopup } =
    useManageTimelinePopup({
      sizes: {
        blockWidth,
        popupWidthAlarm,
        popupWidthAlarmSymbolSize,
        popupWidthNcFileName,
        popupWidthNcFileNameSymbolSize,
        popupWidthProductivityStatus,
        popupWidthProductivityStatusLabel,
        popupWidthProductivityStatusLabelSymbolSize,
      },
    });

  const {
    rules,
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    selectedTime,
    selectRange,
    handleMouseMove,
    onMouseLeave,
    handleMouseDown,
    handleMouseUp,
    lineRef,
    timeXRef,
    sectionRef,
    timelineRef,
    onShowEventsPress,
    eventsContainerRef,
    selectedEventType,
    setSelectedEventType,
    closeEventsForMachine,
    isShowingEventsForMachine,
  } = useManageTimelineActions({ startTime, endTime, yearIsEnabled, fetchData, triggerHidePopup, sizes: { blockWidth } });

  useManageTimelineEffects({
    abortControllerEventStart,
    abortControllerEventEnd,
    abortControllerNcFile,
    abortControllerEvents: undefined, // Multi-machine: handled separately
    abortControllerProductionBatches: undefined, // Multi-machine: handled separately
    startTime,
    endTime,
    yearIsEnabled,
    fetchData,
    shouldDependOnSelectedMachine: false,
  });

  const { selectedTimeCounter, isWithERP } = useTimelineSelectors();

  // Cleanup effect for multi-machine abort controllers
  useEffect(() => {
    return () => {
      // Cleanup all abort controllers for events
      Object.values(abortControllerEvents.current).forEach((controller) => {
        controller?.abort();
      });

      // Cleanup all abort controllers for production batches
      Object.values(abortControllerProductionBatches.current).forEach((controller) => {
        controller?.abort();
      });
    };
  }, [machines.map((m) => m.machineId).join(',')]);

  const content = (
    <div
      data-testid={TEST_IDS.timeline.multiTimelineContainer}
      style={{
        width: '100%',
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        alignContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
        background: C('Background/Light/Field'),
        borderRadius: scale(SCREEN_BORDER_RADIUS),
        boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DateHeader
        {...{
          startTime,
          endTime,
          C,
          setSelectedChartDate: (date: string) => dispatch(setSelectedChartDate(date)),
          setSelectedChartDateEnd: (date: string) => dispatch(setSelectedChartDateEnd(date)),
          selectedTime,
          isZoomInDisabled: rules.isZoomInDisabled,
          isZoomResetDisabled: rules.isZoomResetDisabled,
          setEndTime: (date: string) => dispatch(setSelectedChartDateEnd(date)),
          setStartTime: (date: string) => dispatch(setSelectedChartDate(date)),
          fetchData,
          selectRange,
          selectedTimeCounter,
          resetOnInitialRender: false,
        }}
      />
      <div style={{ width: blockWidth, height: '100%', position: 'relative' }}>
        <div
          id="mainContainer"
          style={{ position: 'relative', width: blockWidth, height: '100%' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={onMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <div
            ref={lineRef}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: 1,
              backgroundColor: C('System/Light/Red50%'),
              zIndex: 1000,
            }}
          >
            <Svg icon="triangle" color="System/Light/Red" height={9} width={13} style={{ position: 'absolute', left: -6 }} />
            <div
              ref={timeXRef}
              style={{
                ...font['Subhead 1'],
                fontSize: 15,
                fontWeight: 400,
                lineHeight: '20px',
                width: 44,
                textAlign: 'center',
                backgroundColor: C('System/Light/Red'),
                color: C('Label/Light/AlwaysLight Primary'),
                zIndex: 1,
                position: 'absolute',
                top: 13,
                left: -26,
                borderRadius: 4,
                fontVariant: ['tabular-nums'],
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                display: 'flex',
                paddingLeft: 4,
                paddingRight: 4,
              }}
            />
          </div>
          <div
            ref={sectionRef}
            style={{
              position: 'absolute',
              height: '100%',
              backgroundColor: 'rgba(0, 120, 215, 0.5)',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          />
          <div style={{ width: '100%', height: 1, background: C('Gray/Light/3') }} />
          <Timeline
            {...{
              C,
              startTime,
              timelineRef,
              blockWidth,
              rules,
            }}
          />

          {/* Render timeline for each machine */}
          {machines.map((machine, index) => {
            const machineData = machineTimelineData[machine.machineId];
            if (!machineData) return null;

            return (
              <div key={machine.machineId} style={{ marginBottom: index < machines.length - 1 ? 20 : 0 }}>
                <Counts
                  {...{
                    C,
                    countsData: {
                      loaded: machineData.productionBatchesAndCounts.loaded,
                      data: machineData.productionBatchesAndCounts.data?.counts,
                    },
                    blockWidth,
                    startTime,
                    rules,
                  }}
                />
                <ProductivityStatus
                  {...{
                    C,
                    events: machineData.events,
                    blockWidth,
                    rules,
                    triggerShowPopup,
                    triggerHidePopup,
                    timelineRef,
                    startTime,
                    showEvents: isShowingEventsForMachine(machine.machineId),
                    title: machine.shortName,
                    machineId: machine.machineId,
                    onShowEventsPress: () => onShowEventsPress(machine.machineId),
                  }}
                />
                {withERP(isWithERP) && !isShowingEventsForMachine(machine.machineId) && (
                  <FeatureFlags {...{ name: 'timeline-jobs' }}>
                    <Jobs
                      {...{
                        C,
                        blockWidth,
                        startTime,
                        rules,
                        batches: machineData.enhancedBatchesData,
                        events: machineData.events,
                      }}
                    />
                  </FeatureFlags>
                )}
                {isShowingEventsForMachine(machine.machineId) && (
                  <div
                    ref={eventsContainerRef}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseUp={(e) => e.stopPropagation()}
                    onMouseMove={(e) => e.stopPropagation()}
                    style={{ position: 'relative', width: '100%' }}
                  >
                    <div style={{ position: 'absolute', top: -2, right: 0, zIndex: 10000 }}>
                      <CloseButton C={C} onPress={() => closeEventsForMachine(machine.machineId)} />
                    </div>
                    <NCFileBatchEventTypeMenu
                      checkIsSelected={(eventType) => eventType === selectedEventType}
                      onPress={(eventType) => setSelectedEventType(eventType)}
                      rowStyles={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginVertical: 12, flex: 1 }}
                      omitItems={OMITTED_EVENT_MENU_ITEMS}
                    />
                    <div>
                      <NCFileBatchEvents
                        eventType={selectedEventType}
                        machineIds={[machine.machineId]}
                        webTabletContainerStyle={{ height: scale(MULTI_TIMELINE_CONFIG.eventsBlockWebTabletHeight) }}
                        startedOn={getUTCFormatOfLocalTime(startTime)}
                        endedOn={getUTCFormatOfLocalTime(endTime)}
                        withoutTimeTiles
                        customOrderBy={TIMELINE_EVENTS_ORDER_BY}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {popupConfigProductivityStatus.show && (
            <NCFileDetailsModalWrapper
              {...{
                setSelectedNCFile: (ncFile: NCFileSearchItem) => dispatch(setSelectedNCFileManufacture(ncFile)),
                C,
                popupType: 'productivityStatus',
                popupConfig: popupConfigProductivityStatus,
                popupWidth: popupWidthProductivityStatus,
                clearPopupTimeout,
                triggerHidePopup,
                abortControllerEventStart,
                abortControllerEventEnd,
                abortControllerNcFile,
              }}
            />
          )}
          {popupConfigProductivityStatusLabel.show && (
            <LabelPopup
              {...{
                C,
                popupName: 'productivityStatusLabel',
                popupConfig: popupConfigProductivityStatusLabel,
                popupWidth:
                  (popupConfigProductivityStatusLabel?.item?.ncFileMain?.length ?? 0) * popupWidthProductivityStatusLabelSymbolSize +
                  popupWidthProductivityStatusLabel,
                popupHeight: popupHeightProductivityStatusLabel,
                clearPopupTimeout,
                triggerHidePopup,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );

  return withScroll ? (
    <ScrollView
      showsVerticalScrollIndicator={false}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        flexGrow: 1,
        alignItems: 'center',
        paddingTop: SCREEN_PADDING_VERTICAL_FOR_ITEM,
        paddingBottom: SCREEN_PADDING_VERTICAL_FOR_ITEM,
        paddingHorizontal: SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
      }}
    >
      {content}
    </ScrollView>
  ) : (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        userSelect: 'none',
        paddingTop: SCREEN_PADDING_VERTICAL_FOR_ITEM,
        width: detailsWidth - SCREEN_PADDING_HORIZONTAL * 2,
        marginInline: 'auto',
        paddingInline: SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
        overflow: 'hidden',
      }}
    >
      {content}
    </div>
  );
};

export default ColorWrapper(MultiMachineTimeline);
